/**
 * InventoryPage.js - 库存管理页
 * 显示所有商品变体的库存情况，支持表格内直接编辑库存
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tag,
  InputNumber,
  message,
  Typography,
  Row,
  Col,
  Select,
  Modal,
} from 'antd';
import {
  SearchOutlined,
  SaveOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { adminInventoryApi } from '../../api/index';

const { Title, Text } = Typography;
const { Option } = Select;

export default function InventoryPage() {
  const [inventory, setInventory] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // 搜索条件
  const [keyword, setKeyword] = useState('');
  const [stockStatus, setStockStatus] = useState(undefined);
  const [sortBy, setSortBy] = useState('quantity_asc');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // 编辑状态：记录正在编辑的行和修改后的值
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [saving, setSaving] = useState(false);

  // 批量编辑模式：是否开启，以及各行改动后的数量 { [variantId]: number }
  const [batchMode, setBatchMode] = useState(false);
  const [batchValues, setBatchValues] = useState({});
  const [batchSaving, setBatchSaving] = useState(false);

  // 历史记录弹窗状态
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyVariant, setHistoryVariant] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 拉取库存列表
  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminInventoryApi.list({
        keyword: keyword || undefined,
        stockStatus: stockStatus || undefined,
        sortBy: sortBy || undefined,
        page,
        pageSize,
      });
      if (Array.isArray(data)) {
        setInventory(data);
        setTotal(data.length);
      } else {
        setInventory(data?.items || data?.list || []);
        setTotal(data?.total || 0);
      }
    } catch (err) {
      message.error('加载库存数据失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [keyword, stockStatus, sortBy, page, pageSize]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // 搜索
  const handleSearch = () => {
    setPage(1);
    fetchInventory();
  };

  // 开始编辑某行
  const startEdit = (record) => {
    setEditingId(record.id);
    setEditingValue(record.inventoryQuantity || 0);
  };

  // 取消编辑
  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue(null);
  };

  // 保存编辑
  const saveEdit = async (record) => {
    if (editingValue === null || editingValue === record.inventoryQuantity) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await adminInventoryApi.update(record.id, editingValue);
      message.success('库存已更新');
      // 更新本地数据
      setInventory(inventory.map(item =>
        item.id === record.id ? { ...item, inventoryQuantity: editingValue } : item
      ));
      cancelEdit();
    } catch (err) {
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 进入批量编辑模式：用当前库存初始化各行的值
  const enterBatchMode = () => {
    cancelEdit();
    const init = {};
    inventory.forEach((it) => { init[it.id] = it.inventoryQuantity ?? 0; });
    setBatchValues(init);
    setBatchMode(true);
  };

  // 退出批量编辑模式，丢弃未保存改动
  const exitBatchMode = () => {
    setBatchMode(false);
    setBatchValues({});
  };

  // 计算相对原始库存有改动的行
  const getBatchChangedItems = () => {
    return inventory
      .filter((it) => batchValues[it.id] !== undefined
        && batchValues[it.id] !== null
        && batchValues[it.id] !== (it.inventoryQuantity ?? 0))
      .map((it) => ({ variantId: it.id, quantity: batchValues[it.id] }));
  };

  // 保存全部改动
  const saveBatch = async () => {
    const items = getBatchChangedItems();
    if (items.length === 0) {
      message.info('没有需要保存的改动');
      return;
    }
    setBatchSaving(true);
    try {
      await adminInventoryApi.batchUpdate(items);
      message.success(`已批量更新 ${items.length} 项库存`);
      // 用函数式更新避免陈旧闭包，确保读到最新 inventory
      const changedMap = {};
      items.forEach((i) => { changedMap[i.variantId] = i.quantity; });
      setInventory((prev) => prev.map((it) =>
        changedMap[it.id] !== undefined ? { ...it, inventoryQuantity: changedMap[it.id] } : it
      ));
      exitBatchMode();
    } catch (err) {
      message.error('批量保存失败：' + (err.message || '未知错误'));
    } finally {
      setBatchSaving(false);
    }
  };

  // 打开历史记录弹窗
  const showHistory = async (record) => {
    setHistoryVariant(record);
    setHistoryVisible(true);
    setHistoryPage(1);
    fetchHistory(record.id, 1);
  };

  // 获取历史记录
  const fetchHistory = async (variantId, page) => {
    setHistoryLoading(true);
    try {
      const data = await adminInventoryApi.getHistory(variantId, { page, pageSize: 10 });
      setHistoryData(data?.items || data?.list || []);
      setHistoryTotal(data?.total || 0);
    } catch (err) {
      message.error('获取历史记录失败：' + (err.message || '未知错误'));
      setHistoryData([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  // 关闭历史弹窗
  const closeHistory = () => {
    setHistoryVisible(false);
    setHistoryVariant(null);
    setHistoryData([]);
    setHistoryTotal(0);
  };

  // 库存状态标签
  const getStockTag = (qty) => {
    if (qty === null || qty === undefined) {
      return <Tag color="default">未设置</Tag>;
    }
    if (qty <= 0) {
      return <Tag color="red">缺货</Tag>;
    }
    if (qty <= 10) {
      return <Tag color="orange">低库存</Tag>;
    }
    return <Tag color="green">充足</Tag>;
  };

  // 表格列配置
  const columns = [
    {
      title: '商品名称',
      dataIndex: 'productTitle',
      key: 'productTitle',
      ellipsis: true,
      render: (text) => <Text strong>{text || '-'}</Text>,
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
      render: (sku) => <Text style={{ fontFamily: 'monospace' }}>{sku || '-'}</Text>,
    },
    {
      title: '变体',
      dataIndex: 'variantTitle',
      key: 'variantTitle',
      width: 150,
      ellipsis: true,
      render: (title) => <Text type="secondary">{title || '默认'}</Text>,
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      align: 'right',
      render: (price) => <Text>${Number(price || 0).toFixed(2)}</Text>,
    },
    {
      title: '库存状态',
      dataIndex: 'inventoryQuantity',
      key: 'stockStatus',
      width: 100,
      align: 'center',
      render: (qty) => getStockTag(qty),
    },
    {
      title: '库存数量',
      dataIndex: 'inventoryQuantity',
      key: 'inventoryQuantity',
      width: 150,
      align: 'center',
      render: (qty, record) => {
        if (batchMode) {
          return (
            <InputNumber
              min={0}
              precision={0}
              value={batchValues[record.id]}
              // 清空时 Ant Design 传入 null，强制转为 0 保持计数与保存行为一致
              onChange={(v) => setBatchValues((prev) => ({ ...prev, [record.id]: v ?? 0 }))}
              size="small"
              style={{ width: 80 }}
            />
          );
        }
        if (editingId === record.id) {
          return (
            <InputNumber
              min={0}
              precision={0}
              value={editingValue}
              onChange={(v) => setEditingValue(v)}
              size="small"
              style={{ width: 80 }}
              autoFocus
            />
          );
        }
        return <Text strong>{qty ?? '-'}</Text>;
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => {
        if (batchMode) {
          return <Text type="secondary">—</Text>;
        }
        if (editingId === record.id) {
          return (
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={() => saveEdit(record)}
              >
                保存
              </Button>
              <Button
                type="link"
                size="small"
                onClick={cancelEdit}
              >
                取消
              </Button>
            </Space>
          );
        }
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              onClick={() => startEdit(record)}
            >
              编辑
            </Button>
            <Button
              type="link"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => showHistory(record)}
            >
              历史
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          库存管理
        </Title>
        {!batchMode ? (
          // loading 期间禁用，防止数据未加载完就进入批量编辑
          <Button type="primary" onClick={enterBatchMode} disabled={loading}>
            批量编辑
          </Button>
        ) : (
          <Space>
            <Button onClick={exitBatchMode}>取消</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={batchSaving}
              onClick={saveBatch}
            >
              保存全部({getBatchChangedItems().length})
            </Button>
          </Space>
        )}
      </div>

      <Card style={{ borderRadius: 8 }}>
        {/* 搜索筛选栏 */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            {/* 批量模式下禁用搜索输入，防止筛选刷新导致 batchValues 与列表不匹配 */}
            <Input
              placeholder="搜索商品名称、SKU"
              prefix={<SearchOutlined />}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              disabled={batchMode}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            {/* 批量模式下禁用库存状态筛选 */}
            <Select
              placeholder="库存状态"
              value={stockStatus}
              onChange={(v) => {
                setStockStatus(v);
                setPage(1);
              }}
              allowClear
              style={{ width: '100%' }}
              disabled={batchMode}
            >
              <Option value="low">低库存 (≤10)</Option>
              <Option value="out">缺货 (≤0)</Option>
              <Option value="sufficient">充足 (>10)</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4}>
            {/* 批量模式下禁用排序选择 */}
            <Select
              placeholder="排序"
              value={sortBy}
              onChange={(v) => {
                setSortBy(v);
                setPage(1);
              }}
              style={{ width: '100%' }}
              disabled={batchMode}
            >
              <Option value="quantity_asc">库存升序</Option>
              <Option value="quantity_desc">库存降序</Option>
              <Option value="product_asc">商品名称 A-Z</Option>
              <Option value="product_desc">商品名称 Z-A</Option>
            </Select>
          </Col>
          <Col xs={12} sm={4}>
            {/* 批量模式下禁用搜索按钮 */}
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              disabled={batchMode}
            >
              搜索
            </Button>
          </Col>
        </Row>

        {/* 库存统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={8} sm={4}>
            <Card size="small" style={{ background: '#f6ffed' }}>
              <Statistic title="充足" value={inventory.filter((v) => v.inventoryQuantity > 10).length} color="#52c41a" />
            </Card>
          </Col>
          <Col xs={8} sm={4}>
            <Card size="small" style={{ background: '#fff7e6' }}>
              <Statistic title="低库存" value={inventory.filter((v) => v.inventoryQuantity > 0 && v.inventoryQuantity <= 10).length} color="#fa8c16" />
            </Card>
          </Col>
          <Col xs={8} sm={4}>
            <Card size="small" style={{ background: '#fff1f0' }}>
              <Statistic title="缺货" value={inventory.filter((v) => v.inventoryQuantity <= 0).length} color="#f5222d" />
            </Card>
          </Col>
        </Row>

        <Table
          dataSource={inventory}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 700 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => {
              if (batchMode && getBatchChangedItems().length > 0) {
                Modal.confirm({
                  title: '放弃未保存的改动？',
                  content: '切换分页会丢失当前页尚未保存的库存改动。',
                  okText: '放弃并切页',
                  cancelText: '留在本页',
                  onOk: () => { exitBatchMode(); setPage(p); },
                });
              } else {
                if (batchMode) exitBatchMode();
                setPage(p);
              }
            },
          }}
          locale={{ emptyText: '暂无库存数据' }}
        />
      </Card>

      {/* 库存变化历史弹窗 */}
      <Modal
        title={null}
        open={historyVisible}
        onCancel={closeHistory}
        footer={null}
        width={700}
        styles={{ body: { padding: 0 } }}
      >
        {/* 头部信息 */}
        <div style={{
          padding: 16,
          background: '#fafafa',
          borderBottom: '1px solid #f0f0f0',
        }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            库存变化历史
          </div>
          {historyVariant && (
            <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#666' }}>
              <span>商品：<strong>{historyVariant.productTitle || '-'}</strong></span>
              <span>SKU：<strong style={{ fontFamily: 'monospace' }}>{historyVariant.sku || '-'}</strong></span>
              <span>变体：<strong>{historyVariant.variantTitle || '默认'}</strong></span>
              <span>当前库存：<strong style={{ color: '#1890ff' }}>{historyVariant.inventoryQuantity ?? '-'}</strong></span>
            </div>
          )}
        </div>

        {/* 历史记录列表 */}
        <div style={{ padding: 16 }}>
          <Table
            dataSource={historyData}
            columns={[
              {
                title: '时间',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 150,
                render: (text) => (
                  <Text style={{ fontFamily: 'monospace', fontSize: 12, color: '#666' }}>
                    {text || '-'}
                  </Text>
                ),
              },
              {
                title: '变更',
                key: 'change',
                width: 180,
                render: (_, record) => {
                  const change = record.quantityChange || 0;
                  const color = change > 0 ? '#52c41a' : change < 0 ? '#f5222d' : '#666';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text style={{ color: '#999' }}>{record.quantityBefore ?? '-'}</Text>
                      <Text style={{ color }} strong>
                        {change > 0 ? `+${change}` : change}
                      </Text>
                      <Text>=</Text>
                      <Text strong>{record.quantityAfter ?? '-'}</Text>
                    </div>
                  );
                },
              },
              {
                title: '类型',
                dataIndex: 'changeType',
                key: 'changeType',
                width: 100,
                render: (type) => {
                  const typeConfig = {
                    manual: { label: '手动调整', color: 'blue' },
                    order_adjustment: { label: '订单调整', color: 'orange' },
                    refund: { label: '退款入库', color: 'green' },
                    transfer: { label: '调拨', color: 'purple' },
                    other: { label: '其他', color: 'default' },
                  };
                  const config = typeConfig[type] || { label: type || '-', color: 'default' };
                  return <Tag color={config.color}>{config.label}</Tag>;
                },
              },
              {
                title: '原因',
                dataIndex: 'reason',
                key: 'reason',
                ellipsis: true,
                render: (text) => <Text type="secondary">{text || '-'}</Text>,
              },
              {
                title: '操作人',
                dataIndex: 'operatorName',
                key: 'operatorName',
                width: 80,
                render: (text) => <Text>{text || '系统'}</Text>,
              },
            ]}
            rowKey="id"
            loading={historyLoading}
            size="small"
            pagination={{
              current: historyPage,
              pageSize: 10,
              total: historyTotal,
              showSizeChanger: false,
              showTotal: (t) => `共 ${t} 条记录`,
              onChange: (p) => {
                setHistoryPage(p);
                fetchHistory(historyVariant.id, p);
              },
            }}
            locale={{ emptyText: '暂无历史记录' }}
          />
        </div>
      </Modal>
    </div>
  );
}

// 简易统计组件
function Statistic({ title, value, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <Text type="secondary" style={{ fontSize: 12 }}>{title}</Text>
      <div style={{ color, fontSize: 20, fontWeight: 600 }}>{value}</div>
    </div>
  );
}